import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import RalliLogo from './RalliLogo';
import { RALLI, RALLI_STRIP } from '../data/ralli';

/**
 * RalliStrip — homepage-only feature band for SMG's in-house product.
 * Distinct green/navy palette so it reads as a sibling brand under SMG,
 * not another agency service.
 */
export default function RalliStrip() {
  const { brand } = RALLI;
  return (
    <section
      id="ralli"
      className="relative overflow-hidden border-t border-white/5"
      style={{ background: `linear-gradient(120deg, #000 0%, ${brand.ink} 55%, #000 100%)` }}
      data-testid="ralli-strip"
    >
      {/* Ambient product glow */}
      <div
        className="pointer-events-none absolute -top-24 -right-24 w-[45vw] h-[45vw] rounded-full blur-3xl opacity-25"
        style={{ background: `radial-gradient(closest-side, ${brand.primary}, transparent 60%)` }}
      />

      <div className="relative px-5 md:px-10 lg:px-14 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-8">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-3.5 h-3.5" style={{ color: brand.primary }} />
            <p className="text-[10px] md:text-[11px] tracking-[0.35em]" style={{ color: brand.primary }}>
              {RALLI_STRIP.eyebrow}
            </p>
            <span
              className="text-[9px] tracking-[0.35em] px-2 py-1"
              style={{ background: `${brand.accent}22`, color: brand.accent, border: `1px solid ${brand.accent}44` }}
            >
              {RALLI_STRIP.status}
            </span>
          </div>

          {/* Official Ralli lockup */}
          <div className="mb-8">
            <RalliLogo size="lg" withGlow />
          </div>

          <h2 className="font-display text-white text-4xl md:text-6xl leading-[1] tracking-tight mb-4 text-balance">
            {RALLI_STRIP.headline}
          </h2>
          <p className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-6" style={{ color: brand.primary }}>
            One Play. One Court. One Opportunity — at a time.
          </p>
          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mb-8">
            {RALLI_STRIP.supporting}
          </p>

          <Link
            to={RALLI_STRIP.ctaTo}
            className="inline-flex items-center gap-2 min-h-[48px] px-6 py-3 text-[11px] tracking-[0.3em] uppercase transition-colors"
            style={{ background: brand.primary, color: brand.ink }}
            data-testid="ralli-strip-cta"
          >
            {RALLI_STRIP.ctaLabel} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Compact product mark on the right — a hand holding a phone with
            the Ralli app open on screen. Framed as a circular lockup with
            an ambient green halo and slow pulse ring to echo the Ralli
            palette without overpowering the shot. */}
        <div className="md:col-span-4 flex justify-center md:justify-end">
          <div
            className="relative w-40 h-40 md:w-52 md:h-52 rounded-full overflow-hidden"
            style={{ border: `1px solid ${brand.primary}44`, boxShadow: `0 0 60px -10px ${brand.primary}44` }}
            data-testid="ralli-strip-mark"
          >
            {/* Pulsing halo */}
            <div
              className="absolute inset-0 rounded-full animate-[ralliPulse_3s_ease-in-out_infinite] pointer-events-none z-10"
              style={{ boxShadow: `0 0 0 0 ${brand.primary}55` }}
            />
            {/* Phone-in-hand lifestyle shot */}
            <img
              src="/assets/ralli-phone-mark.jpg"
              alt="Ralli app running on a phone, courtside"
              loading="lazy"
              decoding="async"
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover select-none"
            />
            {/* Soft vignette so edges blend into the strip */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, transparent 60%, ${brand.ink}99 100%)` }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: `linear-gradient(135deg, ${brand.primary}18, transparent 55%)` }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ralliPulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50% { transform: scale(1.05); opacity: 0.15; }
        }
      `}</style>
    </section>
  );
}
